import React, { useEffect, useState } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { ShieldCheck, Save, CalendarDays, Clock, Laptop, FileText, GitBranch } from "lucide-react";

const PolicyPage = () => {
  const [policy, setPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/policy").then((r) => setPolicy(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setPolicy((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put("/policy", {
        annual_ferie_days: Number(policy.annual_ferie_days),
        annual_rol_hours: Number(policy.annual_rol_hours),
        max_smartworking_per_week: Number(policy.max_smartworking_per_week),
        require_medical_certificate: policy.require_medical_certificate,
        approval_two_levels: policy.approval_two_levels,
        notes: policy.notes,
      });
      setPolicy(res.data);
      toast.success("Policy aggiornata!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  if (!policy) {
    return (
      <div className="min-h-screen bg-[#f0f2f5]"><HRHeader />
        <div className="max-w-[1200px] mx-auto px-4 py-16 text-center text-gray-400">Caricamento policy...</div>
      </div>
    );
  }

  const numCls = "w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-[#16345c]";

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="policy-page">
      <HRHeader />
      <main className="max-w-[900px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><ShieldCheck size={24} /> Policy Assenze</h1>
        <p className="text-[14px] text-gray-500 mb-6">Configura le regole aziendali per ferie, permessi e smart working.</p>

        <form onSubmit={save} className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 mb-1">Ferie annuali (giorni)</label>
              <div className="relative"><CalendarDays size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="number" min="0" step="0.5" value={policy.annual_ferie_days} onChange={(e) => set("annual_ferie_days", e.target.value)} data-testid="policy-ferie" className={numCls} /></div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 mb-1">Permessi ROL (ore)</label>
              <div className="relative"><Clock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="number" min="0" step="0.5" value={policy.annual_rol_hours} onChange={(e) => set("annual_rol_hours", e.target.value)} data-testid="policy-rol" className={numCls} /></div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 mb-1">Smart working / settimana</label>
              <div className="relative"><Laptop size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="number" min="0" max="7" value={policy.max_smartworking_per_week} onChange={(e) => set("max_smartworking_per_week", e.target.value)} data-testid="policy-sw" className={numCls} /></div>
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center justify-between bg-gray-50 rounded-sm px-4 py-3 cursor-pointer">
              <span className="text-[13.5px] font-semibold text-gray-700 flex items-center gap-2"><FileText size={16} className="text-gray-400" /> Certificato medico obbligatorio per malattia</span>
              <input type="checkbox" checked={policy.require_medical_certificate} onChange={(e) => set("require_medical_certificate", e.target.checked)} data-testid="policy-cert" className="accent-[#16345c] w-4 h-4" />
            </label>
            <label className="flex items-center justify-between bg-gray-50 rounded-sm px-4 py-3 cursor-pointer">
              <span className="text-[13.5px] font-semibold text-gray-700 flex items-center gap-2"><GitBranch size={16} className="text-gray-400" /> Approvazione a due livelli per le trasferte (responsabile + HR)</span>
              <input type="checkbox" checked={policy.approval_two_levels} onChange={(e) => set("approval_two_levels", e.target.checked)} data-testid="policy-twolevels" className="accent-[#16345c] w-4 h-4" />
            </label>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">Note e regolamento</label>
            <textarea value={policy.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={4} data-testid="policy-notes" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[14px] outline-none focus:border-[#16345c] resize-none" />
          </div>

          {policy.updated_at && (
            <div className="text-[12px] text-gray-400">Ultimo aggiornamento: {format(new Date(policy.updated_at), "dd MMM yyyy 'ore' HH:mm", { locale: it })}{policy.updated_by ? ` da ${policy.updated_by}` : ""}</div>
          )}

          <button type="submit" disabled={saving} data-testid="policy-save" className="flex items-center justify-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] px-6 py-2.5 rounded-sm transition-colors disabled:opacity-60">
            <Save size={16} /> {saving ? "Salvataggio..." : "Salva policy"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default PolicyPage;
